// @flow
/*
  Used if you want to *autocomplete* on a field with name `displayName` but store the
  resulting selection in another field with name `valueName`.
  For example, autocompleting on the name of an article `displayName` and storing it
  in a field `valueName` with the article's id.
*/
import * as React from 'react';
import { isChrome } from 'utils/browser';
import { isEmpty, updateObject } from 'utils/functional';

import AutocompleteTextInput, {
  type AutocompleteType
} from 'components/AutocompleteTextInput';
import Field from './Field';

const { useCallback } = React;

const AutocompleteInputField = ({
  displayName,
  valueName,
  label,
  description,
  type = 'text',
  mapError,
  autoComplete,
  ...props
}: {
  displayName: string,
  valueName: string,
  label?: string,
  description?: string,
  type?: string,
  mapError?: string => React.Node,
  autoComplete?: string,
  autocompleteType: AutocompleteType
}) => {
  const autoCompleteString =
    autoComplete === 'off' && isChrome() ? 'foobar' : autoComplete;
  const inputProps = autoComplete
    ? updateObject(props, { autoComplete: autoCompleteString })
    : props;
  const customErrorMatch = useCallback(
    errors => errors[valueName] || errors[displayName],
    [valueName, displayName]
  );
  return (
    <Field
      inputProps={inputProps}
      name={displayName}
      label={label}
      description={description}
      mapError={mapError}
      customErrorMatch={customErrorMatch}
      renderInput={({ value, id, qaId, hasError, updateValueByName }) => (
        <AutocompleteTextInput
          id={id}
          name={displayName}
          type={type}
          hasError={hasError}
          data-qa-id={qaId}
          value={value || ''}
          onChange={e => {
            const displayValue = isEmpty(e.target.value) ? '' : e.target.value;
            updateValueByName(displayName, displayValue);
            updateValueByName(valueName, null);
          }}
          onSuggestionSelected={(e, payload) => {
            const { value: v, name } = payload.suggestion;
            updateValueByName(displayName, name);
            updateValueByName(valueName, v);
          }}
          onKeyPress={e => {
            // Prevents form submission for CMS create/edit page.
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          {...inputProps}
        />
      )}
    />
  );
};

export default AutocompleteInputField;
